import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export function useCharacter(id: string | undefined) {
  return useQuery({
    queryKey: ['character', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .eq('id', id!)
        .single()

      if (error) throw error

      const [skills, injuries, weapons, armour, equipment, contacts, biography] = await Promise.all([
        supabase.from('character_skills').select('*').eq('character_id', id!),
        supabase.from('character_injuries').select('*').eq('character_id', id!),
        supabase.from('character_weapons').select('*').eq('character_id', id!),
        supabase.from('character_armour').select('*').eq('character_id', id!),
        supabase.from('character_equipment').select('*').eq('character_id', id!),
        supabase.from('character_contacts').select('*').eq('character_id', id!),
        supabase.from('character_biography').select('*').eq('character_id', id!).order('character_age').order('sequence'),
      ])

      return {
        ...data,
        skills: skills.data ?? [],
        injuries: injuries.data ?? [],
        weapons: weapons.data ?? [],
        armour: armour.data ?? [],
        equipment: equipment.data ?? [],
        contacts: contacts.data ?? [],
        biography: biography.data ?? [],
      }
    },
  })
}

export function useCampaignCharacters(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-characters', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_snapshots')
        .select('*')
        .eq('campaign_id', campaignId!)

      if (error) throw error
      return data
    },
  })
}

export function useSubmitAction(characterId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { action_type: string; payload: Record<string, unknown> }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('actions')
        .insert({
          character_id: characterId,
          performed_by: user.id,
          action_type: params.action_type,
          label: params.action_type,
          payload: params.payload,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character', characterId] })
    },
  })
}

export function useUndoAction(characterId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (actionId: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      // Check undo policy
      const { data: action } = await supabase
        .from('actions')
        .select('undo_policy, is_undone')
        .eq('id', actionId)
        .single()

      if (!action) throw new Error('Action not found')
      if (action.is_undone) throw new Error('Action already undone')
      if (action.undo_policy === 'permanent') throw new Error('This action cannot be undone')

      // Fetch effects to reverse
      const { data: effects } = await supabase
        .from('effects')
        .select('*')
        .eq('action_id', actionId)
        .order('applied_at', { ascending: false })

      // Reverse each effect
      for (const effect of (effects ?? [])) {
        if (effect.operation === 'UPDATE') {
          await supabase
            .from(effect.target_table)
            .update({ [effect.target_field]: effect.old_value })
            .eq('id', effect.target_row_id)
        } else if (effect.operation === 'INSERT' && effect.target_row_id) {
          await supabase
            .from(effect.target_table)
            .delete()
            .eq('id', effect.target_row_id)
        } else if (effect.operation === 'DELETE' && effect.old_value) {
          await supabase
            .from(effect.target_table)
            .insert(effect.old_value as Record<string, unknown>)
        }
      }

      // Mark action as undone
      const { error } = await supabase
        .from('actions')
        .update({
          is_undone: true,
          undone_at: new Date().toISOString(),
          undone_by: user.id,
        })
        .eq('id', actionId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character', characterId] })
    },
  })
}
